import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getJaecooSiteLang, setJaecooSiteLang } from '../../../../utils/cookie';
import styles from './LanguageSelector.module.scss';

const LanguageSelector = ({ languageList = [], currentLanguage: sitecoreCurrentLanguage }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // 从 Sitecore 数据或其他方式获取当前语言
  const getCurrentLanguage = () => {
    // 优先使用 Sitecore 传入的当前语言
    if (sitecoreCurrentLanguage?.fields?.value?.value) {
      return sitecoreCurrentLanguage.fields.value.value;
    }

    // 其次尝试从cookie获取
    const cookieLang = getJaecooSiteLang();
    if (cookieLang) {
      return cookieLang;
    }

    // 从URL路径获取
    const path = location.pathname;
    // 动态检测语言，基于 Sitecore 语言列表
    if (languageList.length > 0) {
      for (const lang of languageList) {
        const langValue = lang.fields?.value?.value;
        if (langValue && path.startsWith(`/${langValue}`)) {
          return langValue;
        }
      }
    }

    // 如果都没有，使用第一个可用语言或默认
    return languageList.length > 0 ? languageList[0].fields?.value?.value || 'en' : 'en';
  };
  // 更新文档语言
  const updateDocumentLang = (lang) => {
    document.documentElement.lang = lang;
  };

  const currentLanguage = getCurrentLanguage();

  if (currentLanguage) {
    updateDocumentLang(currentLanguage);
  }

  // 获取可用的语言列表
  const getAvailableLanguages = () => {
    if (languageList.length > 0) {
      return languageList.map((lang) => ({
        code: lang.fields?.value?.value,
        name: lang.fields?.name?.value,
        id: lang.id,
      }));
    }

    // 如果没有 Sitecore 数据，使用默认语言
    return [
      { code: 'en', name: 'EN', id: 'default-en' },
      { code: 'nl', name: 'NL', id: 'default-nl' },
    ];
  };

  const availableLanguages = getAvailableLanguages();

  // 切换语言
  const switchLanguage = (newLang) => {
    if (newLang === currentLanguage) return;

    const currentPath = location.pathname + location?.search;
    let newPath;

    // 获取所有可用的语言代码
    const allLanguageCodes = availableLanguages.map((lang) => lang.code);

    // 找到当前路径中的语言前缀
    let pathWithoutLang = currentPath;
    let foundCurrentLang = false;

    for (const langCode of allLanguageCodes) {
      const langPrefix = `/${langCode}`;
      if (currentPath.startsWith(langPrefix + '/') || currentPath === langPrefix) {
        pathWithoutLang = currentPath.substring(langPrefix.length);
        foundCurrentLang = true;
        break;
      }
    }

    // 构建新路径
    newPath = `/${newLang}${pathWithoutLang}`;

    // 确保路径格式正确
    if (!pathWithoutLang.startsWith('/') && pathWithoutLang !== '') {
      newPath = `/${newLang}/${pathWithoutLang}`;
    }

    console.log('Language switch:', {
      currentPath,
      currentLanguage,
      newLang,
      pathWithoutLang,
      newPath,
    });
    // 保存语言选择到cookie
    setJaecooSiteLang(newLang);
    updateDocumentLang(newLang);

    navigate(newPath);
    setIsOpen(false);
  };

  // 获取语言显示名称
  const getLanguageDisplayName = (langCode) => {
    const lang = availableLanguages.find((l) => l.code === langCode);
    return lang ? lang.name : langCode.toUpperCase();
  };

  // 点击外部关闭下拉框
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={styles.languageSelector} ref={dropdownRef}>
      <button
        className={styles.languageButton}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Select language"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className={styles.currentLanguage}>{getLanguageDisplayName(currentLanguage)}</span>
        <span className={`${styles.dropdownArrow} ${isOpen ? styles.rotated : ''}`}>▼</span>
      </button>

      {isOpen && (
        <div className={styles.dropdownMenu} role="listbox">
          {availableLanguages.map((language) => (
            <button
              key={language.id}
              className={`${styles.dropdownItem} ${
                currentLanguage === language.code ? styles.active : ''
              }`}
              onClick={() => switchLanguage(language.code)}
              role="option"
              aria-selected={currentLanguage === language.code}
            >
              {language.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
